/** §27 — realtime event names and payloads, shared by server and every client. */
export const VENUE_EVENTS = {
  SESSION_OPENED: 'session.opened',
  SESSION_CLOSED: 'session.closed',
  ORDER_CREATED: 'order.created',
  ORDER_CONFIRMED: 'order.confirmed',
  ORDER_SENT_TO_KITCHEN: 'order.sent_to_kitchen',
  ITEM_PREPARING: 'item.preparing',
  ITEM_READY: 'item.ready',
  ORDER_READY: 'order.ready',
  ITEM_PICKED_UP: 'item.picked_up',
  ITEM_SERVED: 'item.served',
  ORDER_COMPLETED: 'order.completed',
  WAITER_REQUESTED: 'service_request.waiter',
  BILL_REQUESTED: 'service_request.bill',
  SERVICE_REQUEST_UPDATED: 'service_request.updated',
  PAYMENT_COMPLETED: 'payment.completed',
  CHECK_UPDATED: 'check.updated',
  TABLE_STATUS_CHANGED: 'table.status_changed',
  RESOURCE_SESSION_STARTED: 'resource_session.started',
  RESOURCE_SESSION_PAUSED: 'resource_session.paused',
  RESOURCE_SESSION_EXTENDED: 'resource_session.extended',
  RESOURCE_SESSION_ENDED: 'resource_session.ended',
  RESOURCE_SESSION_EXPIRING: 'resource_session.expiring',
  TAKEAWAY_RELEASED: 'takeaway.released',
  TAKEAWAY_READY: 'takeaway.ready',
  TAKEAWAY_HANDED_OVER: 'takeaway.handed_over',
  /**
   * The manager approval queue. It used to ride `spending_approval.*`, the
   * events the guest host queue emitted, reusing two of that payload's fields to
   * reach a manager's socket. The host queue is gone with Party, so the gate now
   * has names and a payload of its own and no longer has to pretend to be one.
   */
  POLICY_APPROVAL_REQUESTED: 'policy_approval.requested',
  POLICY_APPROVAL_RESOLVED: 'policy_approval.resolved',
  /**
   * The device and printer fleet (§11, ADR-0009).
   *
   * `print_job.queued` exists on the server and is deliberately NOT broadcast,
   * so it is deliberately not here either: every kitchen ticket in the venue
   * would fire it, and a channel that never stops is a channel nobody reads.
   * Failure is the news — and there is no `printed` event at all, which is why
   * the spool screen still refreshes on a timer for the happy path.
   */
  PRINT_JOB_FAILED: 'print_job.failed',
  /** Out of attempts. Nobody is going to retry this without a person. */
  PRINT_JOB_DEAD: 'print_job.dead',
  DEVICE_REVOKED: 'device.revoked',
  /** A device reported a version below the tenant's supported floor. */
  DEVICE_UNSUPPORTED_VERSION: 'device.unsupported_version',

  /**
   * §16 own delivery, ADR-0029 — the dispatch board's live feed. One event
   * per transition, not one per state, because every listener reads the same
   * fields regardless of which edge fired. Branch room only: this epic builds
   * no guest-facing socket channel (the customer tracking link is a polled
   * public HTTP route, not a socket room).
   *
   * `delivery.location_updated` is deliberately NOT named here even though
   * the backend's own `VENUE_EVENTS` table reserves it: nothing in
   * `RealtimeService` forwards it to a socket today (confirmed by reading
   * `realtime.service.ts` — no `@OnEvent` handler exists for it, and
   * `DeliveryLocationService.recordPing` never even emits it), so a
   * subscription here would join a room and wait forever, the exact silent
   * failure `useRealtime`'s own doc comment warns about. The dispatch board
   * polls for a driver's live position instead — see `DispatchBoardPanel`.
   */
  DELIVERY_STATUS_CHANGED: 'delivery.status_changed',

  /**
   * §20 — a menu version moving (ADR-0011 §12). One event per targeted branch,
   * never a tenant-wide broadcast, so only the tills whose prices actually
   * changed refetch. **Ungated on the wire**: the payload carries no prices,
   * only the version's identity, status and effective moment — "something
   * changed, refetch" — because gating it on `menu_governance.view` would
   * silence exactly the audience that needs it, a cashier's till, which holds
   * `menu.view` and never the governance key. Never sent to a guest room.
   */
  MENU_VERSION_SUBMITTED: 'menu_version.submitted',
  MENU_VERSION_APPROVED: 'menu_version.approved',
  MENU_VERSION_REJECTED: 'menu_version.rejected',
  MENU_VERSION_SCHEDULED: 'menu_version.scheduled',
  MENU_VERSION_PUBLISHED: 'menu_version.published',
  MENU_VERSION_ROLLED_BACK: 'menu_version.rolled_back',

  /**
   * §5 — a `FiscalDocument` changed status (ADR-0005). One event per
   * transition, emitted from the same transaction that writes it, so a
   * subscriber never sees a status without its trail. This is the only wire
   * between the fiscal subsystem and anything watching realtime — `checks/`
   * and `payments/` still know nothing about it.
   */
  FISCAL_DOCUMENT_UPDATED: 'fiscal_document.updated',

  /**
   * §13 / ADR-0018 — the host queue merges these two streams into one ordered
   * list (`HostQueueService.queue`), so the host desk subscribes to both
   * rather than to a queue-specific event of its own; there isn't one — the
   * queue is a projection, not a stored thing.
   */
  RESERVATION_BOOKED: 'reservation.booked',
  RESERVATION_SEATED: 'reservation.seated',
  WAITLIST_JOINED: 'waitlist.joined',
  WAITLIST_CALLED: 'waitlist.called',
  WAITLIST_SEATED: 'waitlist.seated',
  WAITLIST_CLOSED: 'waitlist.closed',
} as const;

export type VenueEventName = (typeof VENUE_EVENTS)[keyof typeof VENUE_EVENTS];

export interface BaseEventPayload {
  tenantId: string;
  branchId: string;
  occurredAt: string;
  actorId?: string;
}

/** §16 own delivery, ADR-0029 — `delivery.status_changed`'s payload. */
export interface DeliveryEventPayload extends BaseEventPayload {
  deliveryId: string;
  orderId: string;
  status: string;
  driverId: string | null;
  zoneId: string | null;
}

export interface OrderEventPayload extends BaseEventPayload {
  orderId: string;
  orderNumber: string;
  status: string;
  fulfillmentType: string;
  sessionId?: string | null;
  tableId?: string | null;
}

export interface OrderItemEventPayload extends BaseEventPayload {
  orderId: string;
  orderItemId: string;
  itemName: string;
  status: string;
  stationId?: string | null;
  stationName?: string | null;
}

export interface PrintJobEventPayload extends BaseEventPayload {
  printJobId: string;
  destinationId: string;
  printerId: string | null;
  documentType: string;
  status: string;
  attempts: number;
  error: string | null;
}

/**
 * A device leaving service, or reporting a version the tenant will not support.
 *
 * Does NOT extend `BaseEventPayload`, and the reason is the one field: a device
 * legitimately has no branch — a tablet registered before anyone assigned it, or
 * one whose branch was deleted under `onDelete: SetNull`. Forcing a `''` into
 * `branchId` would target `branch:''`, a room nobody is in, so the alert about
 * the unassigned device would be the one nobody hears. Those go to the tenant
 * room instead, which only a tenant-scoped socket can hold.
 */
export interface DeviceFleetEventPayload {
  tenantId: string;
  branchId: string | null;
  occurredAt: string;
  actorId?: string;
  deviceId: string;
  deviceName: string;
  deviceType: string;
  reason?: string | null;
  reportedVersion?: string | null;
  minSupportedVersion?: string | null;
}

export interface TableEventPayload extends BaseEventPayload {
  tableId: string;
  areaId: string;
  floorId: string;
  state: string;
}

export interface ResourceSessionEventPayload extends BaseEventPayload {
  resourceSessionId: string;
  resourceId: string;
  status: string;
  remainingSeconds?: number;
  /** The table this meter is run as part of, when it is run as part of one. */
  tableId?: string | null;
}

export interface ServiceRequestEventPayload extends BaseEventPayload {
  serviceRequestId: string;
  type: string;
  status: string;
  areaId?: string | null;
  tableId?: string | null;
  assigneeId?: string | null;
}

/**
 * A manager-approval request or answer. Reaches the branch room, where the
 * managers are, and the requester's own user room — so an approval raised with
 * no branch behind it still gets an answer back to whoever asked for it.
 */
export interface PolicyApprovalEventPayload extends BaseEventPayload {
  approvalId: string;
  /** The user who asked. Never a guest: this queue is staff business. */
  requestedById: string | null;
  policy: string;
  entityType: string;
  entityId: string | null;
  amount: string;
  status: string;
}

/**
 * §20 — a `MenuVersion` moving. Emitted once per branch it targets.
 *
 * Deliberately carries no price: it is a refetch signal, not a feed. A screen
 * that wants to know WHAT changed calls back into `GET /menu-governance/…`
 * with its own `menu_governance.view` — this payload only says something did.
 */
export interface MenuVersionEventPayload extends BaseEventPayload {
  versionId: string;
  versionNo: number;
  name: string;
  status: string;
  /** ISO, and in the future for a scheduled publish — that is the whole point of it. */
  effectiveFrom: string | null;
  /** How many (branch, item) rules this version carries, for a client deciding whether to refetch. */
  ruleCount: number;
  /** Set on a rollback: the version being reverted. */
  revertsVersionNo?: number | null;
}

/**
 * §5 — a fiscal document's status changed (ADR-0005). Carries no money figure
 * beyond what is already public on the document row; `status`/`previousStatus`
 * are what a reconciliation screen refetches on, the same "something changed,
 * refetch" shape `MenuVersionEventPayload` uses.
 */
export interface FiscalDocumentEventPayload extends BaseEventPayload {
  documentId: string;
  documentNumber: string;
  checkId: string;
  kind: string;
  status: string;
  previousStatus: string | null;
  attempts: number;
  /** Stable error code, never a raw authority message. */
  errorCode?: string | null;
}

/** Socket.IO room names — the only authorization boundary on the wire. */
export const rooms = {
  tenant: (id: string) => `tenant:${id}`,
  branch: (id: string) => `branch:${id}`,
  floor: (id: string) => `floor:${id}`,
  area: (id: string) => `area:${id}`,
  table: (id: string) => `table:${id}`,
  station: (id: string) => `station:${id}`,
  session: (id: string) => `session:${id}`,
  order: (id: string) => `order:${id}`,
  resource: (id: string) => `resource:${id}`,
  user: (id: string) => `user:${id}`,
  guest: (sessionId: string) => `guest:${sessionId}`,
};
